import type { AddFilter, CatalogScope, ListedPiece } from "./catalog-types";

/**
 * Scope and add filter (handoff §7.1, criteria 17.4 to 17.7), as pure functions.
 *
 * The catalog under management does not paginate in the reference phase, so there is no
 * cursor to coordinate and filtering in memory matches the prototype.
 */

/** Literal labels of the "O que exibir" pills. */
export const ADD_FILTER_LABEL: Readonly<Record<AddFilter, string>> = {
  todos: "Todos",
  add: "Adicionados",
  nao: "Não adicionados",
};

/** Literal pill order from the prototype. */
export const ADD_FILTER_ORDER: readonly AddFilter[] = ["todos", "add", "nao"];

export function filterByScope(
  pieces: readonly ListedPiece[],
  scope: CatalogScope,
  addFilter: AddFilter,
): ListedPiece[] {
  if (scope === "dentro") return pieces.filter((piece) => piece.inList);

  if (addFilter === "add") return pieces.filter((piece) => piece.inList);
  if (addFilter === "nao") return pieces.filter((piece) => !piece.inList);

  return [...pieces];
}

export type AddFilterOption = {
  value: AddFilter;
  label: string;
  count: number;
};

/**
 * The available pills, each with its count.
 *
 * "Todos" never disappears, since it is the way out of any slice. The other two only exist
 * with a count above zero (criterion 17.6): a pill leading to zero pieces is a dead end
 * announced as a path.
 */
export function addFilterOptions(pieces: readonly ListedPiece[]): AddFilterOption[] {
  const inList = pieces.filter((piece) => piece.inList).length;

  const counts: Record<AddFilter, number> = {
    todos: pieces.length,
    add: inList,
    nao: pieces.length - inList,
  };

  return ADD_FILTER_ORDER.filter((value) => value === "todos" || counts[value] > 0).map(
    (value) => ({ value, label: ADD_FILTER_LABEL[value], count: counts[value] }),
  );
}

/**
 * The current filter, corrected when the choice stopped existing.
 *
 * Adding the last piece from outside the list would otherwise leave an empty grid behind a
 * filter that no longer means anything. Derived rather than written back, which is what the
 * prototype needed a `setTimeout` for.
 */
export function resolveAddFilter(
  pieces: readonly ListedPiece[],
  requested: AddFilter,
): AddFilter {
  const available = addFilterOptions(pieces).map((option) => option.value);

  return available.includes(requested) ? requested : "todos";
}

/** Counts the mode control shows beside each label. */
export function scopeCounts(pieces: readonly ListedPiece[]): { dentro: number; fora: number } {
  return {
    dentro: pieces.filter((piece) => piece.inList).length,
    fora: pieces.length,
  };
}

/** How many pieces are featured **and** in the list (criterion 17.15). */
export function featuredCount(pieces: readonly ListedPiece[]): number {
  return pieces.filter((piece) => piece.featured && piece.inList).length;
}
